import type { ColumnGroup } from '@talos/kyoko';
import type { AbsoluteSortColumnInput } from './absoluteSortColumnInput';

/**
 * Absolute-sort eligibility for blotter columns whose values are signed magnitudes
 * (position, exposure, PnL, greeks). Both positions and portfolio blotters share the
 * same PnL rules: home-currency Equivalent paths plus instrument-currency PnL fields.
 * Portfolio adds its own position/delta/pivot column-id and field-path rules below.
 */
const GREEK_COLUMN_TYPES = new Set(['delta', 'gamma', 'vega', 'theta', 'rho']);

const NUMERIC_ABSOLUTE_SORT_TYPES = new Set(['size', 'number', ...GREEK_COLUMN_TYPES]);

function isNumericAbsoluteSortColumnType(column: AbsoluteSortColumnInput): boolean {
  const columnType = column.type;
  return columnType != null && NUMERIC_ABSOLUTE_SORT_TYPES.has(columnType);
}

const HOME_CURRENCY_EQUIVALENT_PREFIXES = ['Equivalent.', 'position.Equivalent.', 'gridData.Equivalent.'] as const;

function isPnlAbsoluteSortSegment(segment: string): boolean {
  return segment.endsWith('PnL') || segment.endsWith('PnLDelta');
}

function isHomeCurrencyPnlAbsoluteSortField(field: string | undefined): boolean {
  if (field == null) {
    return false;
  }

  if (field.endsWith('PnLEquivalent')) {
    return true;
  }

  for (const prefix of HOME_CURRENCY_EQUIVALENT_PREFIXES) {
    if (field.startsWith(prefix) && isPnlAbsoluteSortSegment(field.slice(prefix.length))) {
      return true;
    }
  }

  const equivalentFieldSuffix = field.split('.Equivalent.')[1];
  return equivalentFieldSuffix != null && isPnlAbsoluteSortSegment(equivalentFieldSuffix);
}

function isPrefixedInstrumentPnlField(field: string, prefix: string): boolean {
  if (!field.startsWith(prefix) || field.includes('.Equivalent.')) {
    return false;
  }

  const suffix = field.slice(prefix.length);
  if (isPnlAbsoluteSortSegment(suffix)) {
    return true;
  }

  if (suffix.startsWith('PnLTags.EndOfDay.')) {
    return isPnlAbsoluteSortSegment(suffix.slice('PnLTags.EndOfDay.'.length));
  }

  return false;
}

function isInstrumentCurrencyPnlAbsoluteSortField(field: string | undefined): boolean {
  if (field == null) {
    return false;
  }

  if (!field.includes('.') && !field.includes('Equivalent') && isPnlAbsoluteSortSegment(field)) {
    return true;
  }

  if (field.startsWith('SettleGroupValues.')) {
    return isPnlAbsoluteSortSegment(field.slice('SettleGroupValues.'.length));
  }

  if (field.startsWith('PnLTags.EndOfDay.') && !field.includes('.Equivalent.')) {
    return isPnlAbsoluteSortSegment(field.slice('PnLTags.EndOfDay.'.length));
  }

  if (isPrefixedInstrumentPnlField(field, 'position.')) {
    return true;
  }

  return false;
}

function isPnlAbsoluteSortField(field: string | undefined): boolean {
  return isHomeCurrencyPnlAbsoluteSortField(field) || isInstrumentCurrencyPnlAbsoluteSortField(field);
}

function isEodExposureAbsoluteSortField(field: string | undefined): boolean {
  if (field == null) {
    return false;
  }

  return /(?:^|\.)PnLTags\.EndOfDay\.(?:Equivalent\.)?(OpeningPosition|TradePositionDelta)$/.test(field);
}

/** Greek columns are matched by column type, not field path. */
function isGreekAbsoluteSortColumn(column: AbsoluteSortColumnInput): boolean {
  const columnType = column.type;
  return columnType != null && GREEK_COLUMN_TYPES.has(columnType);
}

function withAbsoluteSortIfEligible<C extends AbsoluteSortColumnInput>(
  column: C,
  isEligible: (column: AbsoluteSortColumnInput) => boolean
): C {
  if (!isEligible(column)) {
    return column;
  }

  return { ...column, supportsAbsoluteSort: true } as C;
}

function hasEligibleColumnId(column: AbsoluteSortColumnInput, eligibleIds: ReadonlySet<string>): boolean {
  const columnId = column.id ?? column.field;
  return columnId != null && eligibleIds.has(String(columnId));
}

const POSITIONS_POSITION_COLUMN_IDS = new Set(['Amount', 'position-qty']);

/** Positions blotter: position ids, home-currency exposure, EOD exposure, PnL, and greeks. */
export function isPositionsAbsoluteSortColumn(column: AbsoluteSortColumnInput): boolean {
  if (!isNumericAbsoluteSortColumnType(column)) {
    return false;
  }

  return (
    hasEligibleColumnId(column, POSITIONS_POSITION_COLUMN_IDS) ||
    column.field === 'Equivalent.Amount' ||
    column.field === 'Equivalent.Delta' ||
    isEodExposureAbsoluteSortField(column.field) ||
    isPnlAbsoluteSortField(column.field) ||
    isGreekAbsoluteSortColumn(column)
  );
}

export function withPositionsAbsoluteSort<C extends AbsoluteSortColumnInput>(column: C): C {
  return withAbsoluteSortIfEligible(column, isPositionsAbsoluteSortColumn);
}

const PORTFOLIO_ELIGIBLE_COLUMN_IDS = new Set([
  'PositionUnscaled',
  'PositionHome',
  'Position',
  'DeltaExpNet',
  'DeltaExpGross',
  'DeltaExp',
  'GammaExp',
  'DeltaHome',
]);

function isPortfolioPositionField(field: string): boolean {
  return field === 'gridData.Equivalent.Position' || field.endsWith('.Equivalent.Position');
}

function isPortfolioDeltaExposureField(field: string): boolean {
  return field.includes('CcyDeltaExposure');
}

function isPortfolioGreekHomeField(field: string): boolean {
  return field.endsWith('.Equivalent.Delta') && !field.includes('CcyDeltaExposure');
}

function isPortfolioPivotGreekField(field: string): boolean {
  return field.startsWith('gridData.Pivot.');
}

/** Portfolio blotter: shared PnL rules plus portfolio position, delta-exposure, and pivot paths. */
export function isPortfolioAbsoluteSortColumn(column: AbsoluteSortColumnInput): boolean {
  if (!isNumericAbsoluteSortColumnType(column)) {
    return false;
  }

  const field = column.field;

  return (
    hasEligibleColumnId(column, PORTFOLIO_ELIGIBLE_COLUMN_IDS) ||
    (field != null &&
      (isPortfolioPositionField(field) ||
        isPortfolioDeltaExposureField(field) ||
        isPortfolioGreekHomeField(field) ||
        isPortfolioPivotGreekField(field) ||
        isEodExposureAbsoluteSortField(field) ||
        isPnlAbsoluteSortField(field))) ||
    isGreekAbsoluteSortColumn(column)
  );
}

export function withPortfolioAbsoluteSort<C extends AbsoluteSortColumnInput>(column: C): C {
  return withAbsoluteSortIfEligible(column, isPortfolioAbsoluteSortColumn);
}

export function withPortfolioAbsoluteSortGroup<G extends ColumnGroup>(group: G): G {
  return {
    ...group,
    children: group.children.map(child => withPortfolioAbsoluteSort(child)),
  };
}
